/** @jsxImportSource @emotion/react */
import React from "react";
import {
    PNCVisitTimeline, PNCVisitCard, PNCVisitCardHeader, PNCVisitCardBody,
    PNCVisitCardField, PNCVisitCardLabel, PNCVisitCardValue,
    PNCSectionDivider, PNCSectionLine, PNCSectionLabel,
    PNCFormBadge, PNCEmptyState, PNCEmptyTitle, PNCEmptyDesc,
    PNCSecondaryBtn,
} from "./PostnatalCareStyles";

/* ═══════════════════════════════════════════════════════════════
   PNCHistoryTab — Combined history for Maternal + Newborn visits
   Shows all recorded details with an Edit button on each card.
   ═══════════════════════════════════════════════════════════════ */

/* helper to resolve a value from a { value, label } array */
var resolveLabel = function (list, val) {
    if (!val) return "—";
    var match = list.find(function (item) { return item.value === val; });
    return match ? match.label : val;
};

var boolLabel = function (val) {
    return val ? "Yes" : "No";
};

/* FP method labels (not in the hook constants) */
var FP_METHODS = [
    { value: "coc", label: "Combined Oral Contraceptive (COC)" },
    { value: "pop", label: "Progestogen-Only Pill (POP)" },
    { value: "dmpa", label: "DMPA Injectable (Depo-Provera)" },
    { value: "net_en", label: "NET-EN Injectable" },
    { value: "jadelle", label: "Jadelle Implant" },
    { value: "implanon", label: "Implanon Implant" },
    { value: "copper_iud", label: "Copper IUD" },
    { value: "lng_ius", label: "LNG-IUS (Mirena)" },
    { value: "male_condom", label: "Male Condom" },
    { value: "female_condom", label: "Female Condom" },
    { value: "lam", label: "LAM (Lactational Amenorrhea)" },
    { value: "tubal_ligation", label: "Bilateral Tubal Ligation" },
    { value: "counselled_only", label: "Counselled — Method not yet chosen" },
];

var DBS_RESULTS = [
    { value: "negative", label: "Negative (HIV DNA not detected)" },
    { value: "positive", label: "Positive (HIV DNA detected)" },
    { value: "indeterminate", label: "Indeterminate — Repeat needed" },
];

/* ─── Maternal Visit History Item ─── */
var MaternalVisitItem = function (props) {
    var visit = props.visit;
    var onEdit = props.onEdit;
    var maternal = props.maternal;

    var lochiaLabel = resolveLabel(maternal.LOCHIA_STATUS, visit.lochia);
    var uterineLabel = resolveLabel(maternal.UTERINE_INVOLUTION, visit.uterine_involution);
    var bfLabel = resolveLabel(maternal.BREASTFEEDING_STATUS, visit.breastfeeding_status);
    var perineumLabel = resolveLabel(maternal.PERINEUM_STATUS, visit.perineum_wound);
    var breastLabel = resolveLabel(maternal.BREAST_STATUS, visit.breasts);
    var fpLabel = resolveLabel(FP_METHODS, visit.fp_method_started);

    var complications = visit.complications || [];
    var hasComplications = complications.length > 0 && complications[0] !== "none";
    var complicationLabels = hasComplications
        ? complications.map(function (c) { return resolveLabel(maternal.PNC_COMPLICATION_OPTIONS, c); }).join(", ")
        : "None";

    return (
        <PNCVisitCard>
            <PNCVisitCardHeader>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{
                        width: "28px", height: "28px", borderRadius: "50%",
                        background: "#00695c", color: "#fff", display: "flex",
                        alignItems: "center", justifyContent: "center",
                        fontSize: "0.8rem", fontWeight: 700,
                    }}>
                        {visit.pnc_visit_number}
                    </span>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#333" }}>
                            PNC Visit {visit.pnc_visit_number}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#888" }}>{visit.visit_date}</div>
                    </div>
                </div>
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    {hasComplications && (
                        <PNCFormBadge variant="danger">
                            {complications.length} complication(s)
                        </PNCFormBadge>
                    )}
                    {visit.depression_screening_score !== null && visit.depression_screening_score !== undefined && (
                        <PNCFormBadge variant={visit.depression_screening_score >= 13 ? "danger" : visit.depression_screening_score >= 10 ? "warning" : "success"}>
                            EPDS: {visit.depression_screening_score}
                        </PNCFormBadge>
                    )}
                    <PNCSecondaryBtn
                        onClick={function (e) { e.stopPropagation(); onEdit(visit); }}
                        style={{ padding: "4px 12px", fontSize: "0.75rem" }}
                    >
                        Edit
                    </PNCSecondaryBtn>
                </div>
            </PNCVisitCardHeader>
            <PNCVisitCardBody>
                {/* Vitals */}
                <PNCVisitCardField>
                    <PNCVisitCardLabel>BP</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={visit.bp_systolic >= 140 || visit.bp_diastolic >= 90}>
                        {visit.bp_systolic || "—"}/{visit.bp_diastolic || "—"} mmHg
                    </PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Pulse</PNCVisitCardLabel>
                    <PNCVisitCardValue>{visit.pulse || "—"} bpm</PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Temperature</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={parseFloat(visit.temperature) >= 38}>
                        {visit.temperature || "—"}°C
                    </PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Uterine & Lochia */}
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Uterine Involution</PNCVisitCardLabel>
                    <PNCVisitCardValue>{uterineLabel}</PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Lochia</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={visit.lochia === "offensive" || visit.lochia === "heavy"}>
                        {lochiaLabel}
                    </PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Perineum / Wound</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={visit.perineum_wound === "infected" || visit.perineum_wound === "dehisced"}>
                        {perineumLabel}
                    </PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>DVT Signs</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={visit.dvt_signs}>{boolLabel(visit.dvt_signs)}</PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Breast & Feeding */}
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Breast Exam</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={visit.breasts === "mastitis" || visit.breasts === "abscess"}>
                        {breastLabel}
                    </PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Breastfeeding</PNCVisitCardLabel>
                    <PNCVisitCardValue>{bfLabel}</PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Haemoglobin</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={parseFloat(visit.hb_if_checked) > 0 && parseFloat(visit.hb_if_checked) < 11}>
                        {visit.hb_if_checked ? visit.hb_if_checked + " g/dL" : "—"}
                    </PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Depression */}
                <PNCVisitCardField>
                    <PNCVisitCardLabel>EPDS Score</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={visit.depression_screening_score >= 10}>
                        {visit.depression_screening_score !== null && visit.depression_screening_score !== undefined
                            ? visit.depression_screening_score + "/30"
                            : "—"}
                    </PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Complications */}
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Complications</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={hasComplications}>{complicationLabels}</PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Interventions */}
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Iron & Folate</PNCVisitCardLabel>
                    <PNCVisitCardValue>{boolLabel(visit.iron_folate_given)}</PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Vitamin A</PNCVisitCardLabel>
                    <PNCVisitCardValue>{boolLabel(visit.vitamin_a_given)}</PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>ART Continuation</PNCVisitCardLabel>
                    <PNCVisitCardValue>{boolLabel(visit.art_continuation)}</PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Cervical Screening Referral</PNCVisitCardLabel>
                    <PNCVisitCardValue>{boolLabel(visit.cervical_screening_referral)}</PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Family Planning</PNCVisitCardLabel>
                    <PNCVisitCardValue>{fpLabel}</PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Next Visit */}
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Next Visit Date</PNCVisitCardLabel>
                    <PNCVisitCardValue>{visit.next_visit_date || "—"}</PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Seen By */}
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Seen By</PNCVisitCardLabel>
                    <PNCVisitCardValue>{visit.seen_by || "—"}</PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Notes */}
                {visit.notes && (
                    <PNCVisitCardField style={{ gridColumn: "1 / -1" }}>
                        <PNCVisitCardLabel>Notes</PNCVisitCardLabel>
                        <PNCVisitCardValue>{visit.notes}</PNCVisitCardValue>
                    </PNCVisitCardField>
                )}
            </PNCVisitCardBody>
        </PNCVisitCard>
    );
};

/* ─── Newborn Visit History Item ─── */
var NewbornVisitItem = function (props) {
    var visit = props.visit;
    var onEdit = props.onEdit;
    var newbornPnc = props.newbornPnc;

    var feedLabel = resolveLabel(newbornPnc.FEEDING_STATUS, visit.feeding_status);
    var cordLabel = resolveLabel(newbornPnc.CORD_STATUS, visit.cord_status);
    var jaunLabel = resolveLabel(newbornPnc.JAUNDICE_LEVELS, visit.jaundice);
    var breathLabel = resolveLabel(newbornPnc.BREATHING_STATUS, visit.breathing);
    var activityLabel = resolveLabel(newbornPnc.ACTIVITY_STATUS, visit.activity);
    var dbsLabel = resolveLabel(DBS_RESULTS, visit.dbs_result);

    var dangerSigns = visit.danger_signs || [];
    var hasDangerSigns = dangerSigns.length > 0;
    var dangerLabels = hasDangerSigns
        ? dangerSigns.map(function (d) { return resolveLabel(newbornPnc.NEWBORN_DANGER_SIGNS, d); }).join(", ")
        : "None";

    var immunizations = visit.immunizations_given || [];
    var hasImmunizations = immunizations.length > 0;
    var immunizationLabels = hasImmunizations
        ? immunizations.map(function (i) { return resolveLabel(newbornPnc.IMMUNIZATION_OPTIONS, i); }).join(", ")
        : "None";

    return (
        <PNCVisitCard variant="newborn">
            <PNCVisitCardHeader>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{
                        width: "28px", height: "28px", borderRadius: "50%",
                        background: "#0288d1", color: "#fff", display: "flex",
                        alignItems: "center", justifyContent: "center",
                        fontSize: "0.8rem", fontWeight: 700,
                    }}>
                        {visit.pnc_visit_number}
                    </span>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#333" }}>
                            PNC Visit {visit.pnc_visit_number} (Newborn)
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#888" }}>{visit.visit_date}</div>
                    </div>
                </div>
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    {hasDangerSigns && (
                        <PNCFormBadge variant="danger">{dangerSigns.length} danger sign(s)</PNCFormBadge>
                    )}
                    {hasImmunizations && (
                        <PNCFormBadge variant="success">{immunizations.length} vaccine(s)</PNCFormBadge>
                    )}
                    <PNCSecondaryBtn
                        onClick={function (e) { e.stopPropagation(); onEdit(visit); }}
                        style={{ padding: "4px 12px", fontSize: "0.75rem" }}
                    >
                        Edit
                    </PNCSecondaryBtn>
                </div>
            </PNCVisitCardHeader>
            <PNCVisitCardBody>
                {/* Growth & Vitals */}
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Weight</PNCVisitCardLabel>
                    <PNCVisitCardValue>{visit.weight_grams ? visit.weight_grams + "g" : "—"}</PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Temperature</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={parseFloat(visit.temperature) >= 37.5 || (parseFloat(visit.temperature) > 0 && parseFloat(visit.temperature) < 35.5)}>
                        {visit.temperature ? visit.temperature + "°C" : "—"}
                    </PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Physical Exam */}
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Feeding</PNCVisitCardLabel>
                    <PNCVisitCardValue>{feedLabel}</PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Cord Status</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={visit.cord_status === "infected"}>{cordLabel}</PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Jaundice</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={visit.jaundice === "severe" || visit.jaundice === "moderate"}>
                        {jaunLabel}
                    </PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Breathing</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={visit.breathing === "fast" || visit.breathing === "grunting"}>
                        {breathLabel}
                    </PNCVisitCardValue>
                </PNCVisitCardField>
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Activity / Tone</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={visit.activity === "lethargic"}>{activityLabel}</PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Immunizations */}
                <PNCVisitCardField style={{ gridColumn: "1 / -1" }}>
                    <PNCVisitCardLabel>Immunizations Given</PNCVisitCardLabel>
                    <PNCVisitCardValue>{immunizationLabels}</PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Danger Signs */}
                <PNCVisitCardField style={{ gridColumn: "1 / -1" }}>
                    <PNCVisitCardLabel>Danger Signs</PNCVisitCardLabel>
                    <PNCVisitCardValue warn={hasDangerSigns}>{dangerLabels}</PNCVisitCardValue>
                </PNCVisitCardField>

                {/* HIV-Exposed fields */}
                {(visit.nevirapine_continued || visit.ctx_started || visit.dbs_collected) && (
                    <>
                        <PNCVisitCardField>
                            <PNCVisitCardLabel>Nevirapine Continued</PNCVisitCardLabel>
                            <PNCVisitCardValue>{boolLabel(visit.nevirapine_continued)}</PNCVisitCardValue>
                        </PNCVisitCardField>
                        <PNCVisitCardField>
                            <PNCVisitCardLabel>CTX Started</PNCVisitCardLabel>
                            <PNCVisitCardValue>{boolLabel(visit.ctx_started)}</PNCVisitCardValue>
                        </PNCVisitCardField>
                        <PNCVisitCardField>
                            <PNCVisitCardLabel>DBS Collected</PNCVisitCardLabel>
                            <PNCVisitCardValue>{boolLabel(visit.dbs_collected)}</PNCVisitCardValue>
                        </PNCVisitCardField>
                        {visit.dbs_collected && (
                            <PNCVisitCardField>
                                <PNCVisitCardLabel>DBS Result</PNCVisitCardLabel>
                                <PNCVisitCardValue warn={visit.dbs_result === "positive"}>
                                    {visit.dbs_result ? dbsLabel : "Pending"}
                                </PNCVisitCardValue>
                            </PNCVisitCardField>
                        )}
                    </>
                )}

                {/* Seen By */}
                <PNCVisitCardField>
                    <PNCVisitCardLabel>Seen By</PNCVisitCardLabel>
                    <PNCVisitCardValue>{visit.seen_by || "—"}</PNCVisitCardValue>
                </PNCVisitCardField>

                {/* Notes */}
                {visit.notes && (
                    <PNCVisitCardField style={{ gridColumn: "1 / -1" }}>
                        <PNCVisitCardLabel>Notes</PNCVisitCardLabel>
                        <PNCVisitCardValue>{visit.notes}</PNCVisitCardValue>
                    </PNCVisitCardField>
                )}
            </PNCVisitCardBody>
        </PNCVisitCard>
    );
};

/* ═══ MAIN COMPONENT ═══ */
export var PNCHistoryTab = function (props) {
    var maternal = props.maternal;
    var newbornPnc = props.newbornPnc;
    var onEditMaternal = props.onEditMaternal;
    var onEditNewborn = props.onEditNewborn;

    var maternalVisits = maternal.pncVisits || [];
    var newbornVisits = newbornPnc.pncVisits || [];
    var totalVisits = maternalVisits.length + newbornVisits.length;

    if (totalVisits === 0) {
        return (
            <PNCEmptyState>
                <PNCEmptyTitle>No Visit History</PNCEmptyTitle>
                <PNCEmptyDesc>
                    No PNC visits have been recorded yet. Use the Maternal or Newborn tabs to create assessments.
                </PNCEmptyDesc>
            </PNCEmptyState>
        );
    }

    return (
        <div>
            {/* ─── Maternal Visit History ─── */}
            {maternalVisits.length > 0 && (
                <div>
                    <PNCSectionDivider>
                        <PNCSectionLine />
                        <PNCSectionLabel>Maternal PNC Visits ({maternalVisits.length})</PNCSectionLabel>
                        <PNCSectionLine />
                    </PNCSectionDivider>
                    <PNCVisitTimeline>
                        {maternalVisits.slice().reverse().map(function (visit) {
                            return (
                                <MaternalVisitItem
                                    key={visit.id}
                                    visit={visit}
                                    onEdit={onEditMaternal}
                                    maternal={maternal}
                                />
                            );
                        })}
                    </PNCVisitTimeline>
                </div>
            )}

            {/* ─── Newborn Visit History ─── */}
            {newbornVisits.length > 0 && (
                <div style={{ marginTop: maternalVisits.length > 0 ? "24px" : 0 }}>
                    <PNCSectionDivider>
                        <PNCSectionLine />
                        <PNCSectionLabel>Newborn PNC Visits ({newbornVisits.length})</PNCSectionLabel>
                        <PNCSectionLine />
                    </PNCSectionDivider>
                    <PNCVisitTimeline>
                        {newbornVisits.slice().reverse().map(function (visit) {
                            return (
                                <NewbornVisitItem
                                    key={visit.id}
                                    visit={visit}
                                    onEdit={onEditNewborn}
                                    newbornPnc={newbornPnc}
                                />
                            );
                        })}
                    </PNCVisitTimeline>
                </div>
            )}
        </div>
    );
};
